import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import AddUserDialog from 'views/Users/common/AddUserDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/accountDetailsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import invalidateCache from 'utils/api/invalidateCache';
import { useDispatch, useSelector } from 'react-redux';
import { throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
	accountPermissionsSelector,
	departmentPermissionsSelector,
	userTypeSelector,
} from 'redux/modules/session/selectors';
import AdditionalInfoDialog from './subcomponents/AdditionalInfoDialog';
import AddressesService from 'services/addresses/addresses';
import { hasAccess } from 'utils/permissions/permissions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { useHistory } from 'react-router-dom';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleUser, UserType } from 'utils/api/models/users/singleUser';
import { AccountDetails } from 'utils/api/models/accounts/accountDetails';
import OrganizationManagersCard from './subcomponents/OrganizationManagersCard';
import OrganizationAdminsCard from './subcomponents/OrganizationAdminsCard';
import OrganizationAddress from './subcomponents/OrganizationAddress';
import ContactInformation from './subcomponents/ContactInformation';
import OrganizationDetailsHeader from './subcomponents/OrganizationDetailsHeader';
import AccountManagers from './subcomponents/AccountManagers';
import OrganizationTaxId from './subcomponents/OrganizationTaxId';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IOrganizationDetailsSubpageProps {
	departments: SingleDepartment[];
	accounts: SingleCorpAccount[];
	match: {
		params: {
			accountId: string;
		};
	};
}

const Details = (props: IOrganizationDetailsSubpageProps) => {
	const classes = useStyles();
	const { accountId } = props.match.params;
	const { departments, accounts } = props;
	const [account, setAccount] = useState<types.organizations.IAccount | null>(null);
	const [accountPhoto, setAccountPhoto] = useState<string | undefined>();
	const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState<boolean>(false);
	const [userType, setUserType] = useState<UserType>();
	const [openEditAdditionalInfo, setOpenEditAdditionalInfo] = useState(false);
	const [formData, setFormData] = useState<{ [index: string]: never | string }>({});
	const [accountsGovernorate, setAccountsGovernorate] = useState('');
	const history = useHistory();
	const dispatch = useDispatch();
	const accountPermissions = useSelector(accountPermissionsSelector);
	const departmentPermissions = useSelector(departmentPermissionsSelector);
	const loggedUserType = useSelector(userTypeSelector);
	const isMounted = useRef(true);

	const findDepartment = useCallback(
		(departmentId: string) => {
			return departments.find((department) => department.deptId === departmentId);
		},
		[departments]
	);

	const fetchAccountDetails = useCallback(() => {
		dispatch(showLoadingIndicator());
		AccountsService.getCorpAccountDetails(accountId)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe((account: AccountDetails) => {
				if (isMounted.current) {
					const managers = (account.managers || []).map((manager: SingleUser) => ({
						...manager,
						department: findDepartment(manager.departmentId),
						accessible: hasAccess(manager, loggedUserType, accountPermissions, departmentPermissions),
					}));
					const admins = (account.admins || []).map((admin: SingleUser) => ({
						...admin,
						department: findDepartment(admin.departmentId),
						accessible: hasAccess(admin, loggedUserType, accountPermissions, departmentPermissions),
					}));
					setAccount({
						...account,
						managers,
						admins,
					});
				}
			});
	}, [dispatch, accountId, findDepartment, loggedUserType, accountPermissions, departmentPermissions]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		fetchAccountDetails();
	}, [fetchAccountDetails]);

	useEffect(() => {
		AccountsService.getCorpAccountPhoto(accountId).subscribe(
			({ response }) => isMounted.current && setAccountPhoto(response)
		);
	}, [accountId]);

	useEffect(() => {
		AddressesService.getRegions().subscribe(({ response }) => {
			if (isMounted.current && account?.accountDetails?.region) {
				const initialRegion = response.find((region: any) => region.key === account.accountDetails.region);
				setAccountsGovernorate(initialRegion?.region);
			}
		});
	}, [account]);

	const setPhoto = (base64photo: string) => {
		dispatch(showLoadingIndicator());
		AccountsService.setCorpAccountPhoto({
			base64photo,
			accountId,
		}).subscribe(
			({ response }) => {
				if (response.arguments.returnValue) {
					setAccountPhoto(base64photo);
					invalidateCache(['getCorpAccountPhoto'], true);
				}
			},
			(error) => throwError(error),
			() => dispatch(hideLoadingIndicator())
		);
	};

	const userAddedSuccessfully = () => {
		fetchAccountDetails();
	};

	const addressSuccessfullyUpdated = () => {
		setOpenEditAdditionalInfo(false);
		fetchAccountDetails();
	};

	const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const reader = new FileReader();
		const target = e.target as HTMLInputElement;
		const file = target.files![0];
		reader.onloadend = () => {
			setPhoto(reader.result as string);
		};
		if (file) {
			reader.readAsDataURL(file);
		}
	};

	const showAll = (userType: 'admins' | 'managers') => history.push(`organization-details/${userType}`);

	const setFormCardData = (name: string, value: string) =>
		setFormData((formData: { [index: string]: never | string }) => ({ ...formData, [name]: value }));

	const findAccount = (id: string) => {
		return accounts.find((account) => account.accountId === id);
	};

	const onClickAddManager = () => {
		setUserType('MANAGER');
		setIsAddUserDialogOpen(true);
	};

	const onClickAddAdmin = () => {
		setUserType('ADMIN');
		setIsAddUserDialogOpen(true);
	};

	const editAdditionalInfo = () => {
		setFormData({
			accountId: accountId,
			governorate: account!.accountDetails!.region!,
			region: account!.accountDetails!.region!,
			city: account!.accountDetails!.city!,
			way: account!.accountDetails!.way!,
			house: account!.accountDetails!.house!,
			apartment: account!.accountDetails!.apartment!,
			landmark: account!.accountDetails!.landmark!,
		});
		setOpenEditAdditionalInfo(true);
	};

	return (
		<div className={classes.wrapper}>
			<ConditionalRender
				show={!!account}
				onTrue={() => (
					<>
						<OrganizationDetailsHeader
							account={account!}
							accountPhoto={accountPhoto}
							handleImageChange={handleImageChange}
							setPhoto={setPhoto}
						/>
						<GridContainer justify="flex-start">
							<GridItem xs={12} sm={6}>
								<GridContainer justify="flex-start">
									<GridItem xs={12} sm={12}>
										<ContactInformation account={account!} />
									</GridItem>
									<GridItem xs={12} sm={12}>
										<OrganizationTaxId account={account!} />
									</GridItem>
								</GridContainer>
							</GridItem>
							<GridItem xs={12} sm={6}>
								<OrganizationAddress
									editAdditionalInfo={editAdditionalInfo}
									account={account!}
									loggedUserType={loggedUserType}
									accountsGovernorate={accountsGovernorate}
								/>
							</GridItem>
							<GridItem xs={12} sm={6}>
								<OrganizationAdminsCard
									showAllAdmins={() => showAll('admins')}
									account={account!}
									addAdmin={onClickAddAdmin}
								/>
							</GridItem>
							<GridItem xs={12} sm={6}>
								<OrganizationManagersCard
									showAllManagers={() => showAll('managers')}
									account={account!}
									addManager={onClickAddManager}
								/>
							</GridItem>
							<GridItem xs={12}>
								<AccountManagers
									documentId={account!.accountDetails.documentId}
									documentType={account!.accountDetails.documentType}
								/>
							</GridItem>
						</GridContainer>
						<ConditionalRender
							show={!!userType}
							onTrue={() => (
								<AddUserDialog
									isDialogOpen={isAddUserDialogOpen}
									setIsDialogOpen={(value) => setIsAddUserDialogOpen(value)}
									dialogHeader={translate(`add.${userType!.toLowerCase()}`)}
									userType={userType}
									accounts={accounts}
									departments={departments as any}
									account={findAccount(accountId)}
									userAddedSuccessfully={userAddedSuccessfully}
									toastSuccessMessage={translate(`${userType!.toLowerCase()}.user.added`)}
								/>
							)}
						/>
						<ConditionalRender
							show={openEditAdditionalInfo}
							onTrue={() => (
								<AdditionalInfoDialog
									isOpen={openEditAdditionalInfo}
									onClose={() => setOpenEditAdditionalInfo(false)}
									formData={formData}
									setFormCardData={setFormCardData}
									addressSuccessfullyUpdated={addressSuccessfullyUpdated}
								/>
							)}
						/>
					</>
				)}
			/>
		</div>
	);
};

export default Details;
